import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { fetchTagsIfNeeded } from '@lootboy/lib/actions/TagActions';

import CloseBorderedButton from '~/components/ButtonIcons/CloseBorderedButton';
import ContentView from '~/components/ContentView';
import GoogleTagManager, {
  LOOTPACK_PATH,
  useGoogleTagManagerPage,
} from '~/components/GoogleTagManager';
import LootBoyButton from '~/components/LootBoyButton';
import BundleCampaignProvider from '~/components/LootPackViews/BundleSelector/BundleCampaignProvider';
import LootHeader from '~/components/LootPackViews/LootDetailView/LootHeader';
import LootPacksNavigationBar from '~/components/LootPackViews/LootPacksNavigationBar';
import PopupContent from '~/components/PopupContent';
import SubscriptionRedirectPopup from '~/components/SubscriptionPopup/SubscriptionRedirectPopup';

import { INTERNAL_CURRENCIES } from '~/constants/payments';
import useBooleanState from '~/hooks/useBooleanState';
import { useClosePopup } from '~/hooks/useClosePopup';
import { useRedirectToLogin } from '~/hooks/useRedirectToLogin';
import useIsSubscribedSelector from '~/redux/hooks/useIsSubscribedSelector';
import { getCurrencyUnit } from '~/utils';

import BundleSelector from '../BundleSelector';
import ExchangeCurrencyDialog from '../ExchangeCurrencyDialog';
import { useLootPackHook } from '../shared';
import AppropriateCountdown from './AppropriateCountdown';
import LootCardCarousel from './LootCardCarousel';

import styleLootpacks from '../LootPackViews.module.scss';
import style from './LootDetailView.module.scss';

const LootDetailView = () => {
  useGoogleTagManagerPage(LOOTPACK_PATH);

  const history = useHistory();
  const dispatch = useDispatch();
  const closePopup = useClosePopup();
  const redirectToLogin = useRedirectToLogin();

  const [showDialog, setShowDialog] = useState(false);
  const [showSeasonTicketDialog, changeShowSeasonTicketDialog] =
    useBooleanState();
  const isSubscribed = useIsSubscribedSelector();

  const {
    loading,
    _id,
    name,
    detailBanner,
    headline,
    subHeadline,
    availableFrom,
    availableUntil,
    content,
    cardTemplates,
    InfoMessage,
    bundleSelected,
    setBundleSelected,
    bundles,
    buttonSettings,
    priceLabel,
    bonuses,
    isLoggedIn,
    price,
    currency,
    newBalance,
    onPurchase,
    processing,
    separatorTag,
    lootTypeTag,
    countryIcon,
    bundleImage,
    isSeasonTicket,
  } = useLootPackHook({ changeShowSeasonTicketDialog });

  useEffect(() => {
    if (_id) {
      GoogleTagManager.dataLayer({
        event: 'lootpack_visited',
        loot_id: _id,
        loot_name: name,
      });
    }
  }, [_id, name]);

  useEffect(() => {
    dispatch(fetchTagsIfNeeded()).catch(() => {});
  }, [dispatch]);

  const {
    alertMessage,
    title = I18n.t('LootDetailView_purchase_button_lootgem'),
    ...buttonProps
  } = buttonSettings;
  const onButtonPressed = () => {
    if (!isLoggedIn) {
      redirectToLogin();
    } else if (isSeasonTicket && !isSubscribed) {
      changeShowSeasonTicketDialog.setTrue();
    } else if (!INTERNAL_CURRENCIES.includes(currency)) {
      history.push({
        pathname: `/loots/${_id}/purchase`,
        state: { bundleSelected },
      });
    } else {
      setShowDialog(true);
    }
  };

  return (
    <>
      <PopupContent
        loading={loading}
        onClose={closePopup}
      >
        <div className={styleLootpacks.content}>
          <LootPacksNavigationBar
            onClose={closePopup}
            item={{
              name,
              _id,
            }}
            itemName="loot"
            Button={CloseBorderedButton}
          />
          <LootHeader
            countryIcon={countryIcon}
            bundleImage={bundleImage}
            name={name}
            headline={headline}
            detailBanner={detailBanner}
            separatorTag={separatorTag}
            lootTypeTag={lootTypeTag}
            subHeadline={subHeadline}
          />
          {InfoMessage ? (
            <InfoMessage />
          ) : (
            <>
              <BundleCampaignProvider
                _id={_id}
                currency={currency}
                bundleSelected={bundleSelected}
                lootBundles={bundles}
              >
                <BundleSelector
                  bonuses={bonuses}
                  bundles={bundles}
                  bundleSelected={bundleSelected}
                  onSelectBundle={setBundleSelected}
                  className={style.bundleSelector}
                />
              </BundleCampaignProvider>
              <div className={style.purchase}>
                <div>
                  <span
                    className={classNames(style.price, styleLootpacks.price, {
                      [(currency || '').toLowerCase()]: 1,
                    })}
                  >
                    {priceLabel}
                  </span>
                  {getCurrencyUnit(currency) && (
                    <span>{I18n.t('LootDetailView_purchase_tax')}</span>
                  )}
                </div>
                <div>
                  <LootBoyButton
                    {...buttonProps}
                    title={title}
                    onClick={onButtonPressed}
                    scaleTitle
                    className={style.purchaseButton}
                  />
                </div>
              </div>
              {alertMessage && (
                <p className={styleLootpacks.currencyAlert}>{alertMessage}</p>
              )}
            </>
          )}
          <AppropriateCountdown
            availableFrom={availableFrom}
            availableUntil={availableUntil}
          />
          <div className={style.lootContent}>
            {(content || []).map((c, index) => (
              <ContentView
                {...c}
                key={index}
              />
            ))}
            <LootCardCarousel cardTemplates={cardTemplates} />
          </div>
        </div>
      </PopupContent>
      <ExchangeCurrencyDialog
        isLoggedIn={isLoggedIn}
        onClose={() => setShowDialog(false)}
        show={showDialog}
        currency={currency}
        price={price}
        lootId={_id}
        bundleSelected={bundleSelected}
        balance={newBalance}
        onPurchase={onPurchase}
        processing={processing}
      />
      <SubscriptionRedirectPopup
        show={showSeasonTicketDialog}
        onClose={changeShowSeasonTicketDialog.setFalse}
      />
    </>
  );
};

export default LootDetailView;
